/*
 * Validators
 * ------------------------------------------------------------------------------------------------
 */
import { getAge } from './getAge'

export const VALIDATOR_TYPE_AGE = 'age';
export const VALIDATOR_TYPE_ZIPCODE = 'zipcode';
export const VALIDATOR_PASSWORD = 'password';
export const VALIDATOR_NON_EMPTY = 'non-empty';
export const VALIDATOR_BIRTHDAY = 'birthday';
export const VALIDATOR_EMAIL = 'email';
export const VALIDATOR_EMAIL_SIMPLE = 'simple-email';
export const VALIDATOR_NUMERIC_BOUNDARY = 'numeric_boundary';

export const INPUT_ERROR_BLANK = 'Please answer all questions';
export const PASSWORD_ERROR = 'Password does not meet the expectations.';
export const PASSWORD_CONFIRM_ERROR = 'The password confirmation does not match.';
export const BIRTHDAY_VALIDATION_ERROR = 'is not a valid birthday';
export const BIRTHDAY_UNDERAGE_ERROR = 'Sorry you must be over 18';
export const EMAIL_VALIDATION_ERROR = 'Email is not valid';
export const EMAIL_SIMPLE_VALIDATION_ERROR = (answer: string) => `"${answer}" is not a valid email`;

const PASS_REGEX = new RegExp('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$')
const EMAIL_REGEX = /^.+@.+\..+$/

export interface Error {
  key: string
  text: string
  answer: any
}

export function validateZipcode(inputData, answer) {
  if ((answer + '').length === 5) {
    return false;
  }

  return {
    key: inputData.key,
    text: `'${answer}' is not a valid zip code`,
    answer
  };
}

export function validateAge(inputData, answer) {
  if (parseInt(answer, 10) < 13) {
    return {
      key: inputData.key,
      text: 'Sorry you must be 13+ to continue',
      answer
    }
  }

  return false;
}

export function validatePassword(inputData: QuestionDataInput, answer: string, prevInputData?: QuestionDataInput, prevAnswer?: string): false | Error {
  let isReqValid = true
  let matchesConfirmation = true

  if (inputData.type === 'password') {
    isReqValid = answer.length >= 8 && PASS_REGEX.test(answer);
  }
  if (inputData.type === 'password-requirements' && prevInputData && prevInputData.type === 'password') {
    matchesConfirmation = prevAnswer === answer
  }
  return !isReqValid || !matchesConfirmation
    ? {
      key: inputData.key,
      text: !isReqValid ? PASSWORD_ERROR : PASSWORD_CONFIRM_ERROR,
      answer
    }
    : false
}

export function validateNonEmpty(inputData: QuestionDataInput, answer: string): false | Error {
  return !answer.trim().length
    ? {
      key: inputData.key,
      text: INPUT_ERROR_BLANK,
      answer
    }
    : false
}

export function validateBirthday(inputData: QuestionDataInput, answer: string): false | Error {
  const [month, day, year] = answer.split('/')
  const date = new Date(Number(year), Number(month), Number(day))

  let isValidDate = year &&
    month &&
    day &&
    +year &&
    +month &&
    +day &&
    year.length === 4 &&
    (month.length === 1 || month.length === 2) &&
    (day.length === 1 || day.length === 2) &&
    Number(month) <= 12 &&
    Number(day) <= 31 &&
    date.getDate() !== NaN

  const age = getAge(date)

  if (!isValidDate || age >= 120) {
    return {
      key: inputData.key,
      text: `${answer} ${BIRTHDAY_VALIDATION_ERROR}`,
      answer
    }
  }

  if (age < 18) {
    return {
      key: inputData.key,
      text: BIRTHDAY_UNDERAGE_ERROR,
      answer
    }
  }

  return false
}

export function validateEmail(inputData: QuestionDataInput, answer: string): false | Error {
  return EMAIL_REGEX.test(answer)
    ? false
    : {
      key: inputData.key,
      text: EMAIL_VALIDATION_ERROR,
      answer
    }
}

export function validateEmailSimple(inputData: QuestionDataInput, answer: string): false | Error {
  return answer.includes('@')
    ? false
    : {
      key: inputData.key,
      text: EMAIL_SIMPLE_VALIDATION_ERROR(answer),
      answer
    }
}

export function validateNumber(inputData: QuestionDataInput, answer: string): false | Error {
  if (inputData.inputProps && inputData.inputProps.min && inputData.inputProps.min > +answer) {
    return {
      key: inputData.key,
      text: `Please enter a value greater than ${inputData.inputProps.min}`,
      answer
    }
  }

  if (inputData.inputProps && inputData.inputProps.max && inputData.inputProps.max < +answer) {
    return {
      key: inputData.key,
      text: `Please enter a value smaller than ${inputData.inputProps.max}`,
      answer
    }
  }

  return false
}

export function validateQuestionInput(inputData, answer, prevInputData?, prevAnswer?) {
  if ((typeof (answer) === 'undefined' || answer === '') && !inputData.optional) {
    return {
      key: inputData.key,
      text: INPUT_ERROR_BLANK,
      answer
    };
  }

  if (!inputData.validator) {
    return false;
  }

  switch (inputData.validator) {
    case VALIDATOR_TYPE_ZIPCODE: return validateZipcode(inputData, answer);
    case VALIDATOR_TYPE_AGE: return validateAge(inputData, answer);
    case VALIDATOR_PASSWORD: return validatePassword(inputData, answer, prevInputData, prevAnswer);
    case VALIDATOR_NON_EMPTY: return validateNonEmpty(inputData, answer);
    case VALIDATOR_BIRTHDAY: return validateBirthday(inputData, answer);
    case VALIDATOR_EMAIL: return validateEmail(inputData, answer);
    case VALIDATOR_EMAIL_SIMPLE: return validateEmailSimple(inputData, answer);
    case VALIDATOR_NUMERIC_BOUNDARY: return validateNumber(inputData, answer);
  }

  return false;
}
